import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import FileUpload from "@/components/admin/FileUpload";
import { FileGrid } from "@/components/admin/PrivateFile";
import {
  PART_CATEGORIES, PART_STATUSES, PART_CONDITIONS, AUTH_STATUSES,
  titleCase, canSeeFinancials,
} from "@/lib/format";
import { toast } from "sonner";
import { ArrowLeft, Lock } from "lucide-react";

const EMPTY = {
  title: "", part_category: "other", brand: "", model: "", reference_number: "", part_number: "",
  compatible_references: [], compatible_models: [], condition: "", origin: "",
  authentication_status: "not_started", quantity: 0, price: "", price_on_request: false,
  currency: "usd", status: "available", public_photos: [], description: "",
  cost_basis: "", source_seller: "", internal_notes: "", authentication_documents: [],
};

const numOrNull = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
const toList = (s) => (Array.isArray(s) ? s : String(s || "").split(",").map((x) => x.trim()).filter(Boolean));
const listToText = (l) => (Array.isArray(l) ? l.join(", ") : (l || ""));

function SectionTitle({ children, locked }) {
  return (
    <div className="flex items-center gap-2 mt-8 mb-3">
      <h2 className="text-sm font-semibold uppercase tracking-wider text-[var(--text-2)]">{children}</h2>
      {locked && <Badge variant="outline" className="bg-[var(--panel)] text-[#c9a86a] border-[var(--border-2)] text-[10px]"><Lock size={10} className="mr-1" />PRIVATE</Badge>}
    </div>
  );
}

function TextField({ label, value, onChange, type = "text", placeholder, testId }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-[var(--text-2)]">{label}</Label>
      <Input type={type} value={value ?? ""} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)} className="bg-[var(--panel)]" data-testid={testId} />
    </div>
  );
}

export default function AdminPartForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const { user } = useAdminAuth();
  const showFin = canSeeFinancials(user?.role);
  const [form, setForm] = useState(EMPTY);
  const [compatRefText, setCompatRefText] = useState("");
  const [compatModelText, setCompatModelText] = useState("");
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/admin/parts/${id}`).then((r) => {
      setForm({ ...EMPTY, ...r.data });
      setCompatRefText(listToText(r.data.compatible_references));
      setCompatModelText(listToText(r.data.compatible_models));
      setLoading(false);
    }).catch(() => { toast.error("Part not found"); navigate("/admin/parts"); });
  }, [id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.title) { toast.error("Title is required"); return; }
    setSaving(true);
    const payload = {
      ...form,
      price: numOrNull(form.price),
      quantity: form.quantity === "" ? 0 : Number(form.quantity),
      cost_basis: numOrNull(form.cost_basis),
      origin: form.origin || null,
      compatible_references: toList(compatRefText),
      compatible_models: toList(compatModelText),
    };
    if (!showFin) delete payload.cost_basis;
    try {
      if (isEdit) await api.put(`/admin/parts/${id}`, payload);
      else await api.post("/admin/parts", payload);
      toast.success(isEdit ? "Part updated" : "Part created");
      navigate("/admin/parts");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (loading) return <div className="p-8 text-[var(--muted)]">Loading…</div>;

  return (
    <div data-testid="part-form" className="max-w-4xl">
      <button onClick={() => navigate("/admin/parts")} className="flex items-center gap-2 text-sm text-[var(--text-2)] hover:text-[var(--text)] mb-4">
        <ArrowLeft size={16} /> Back to Parts
      </button>
      <div className="flex items-center justify-between mb-2">
        <h1 className="text-2xl font-semibold">{isEdit ? (form.title || "Edit Part") : "Add Part"}</h1>
        <Button onClick={save} disabled={saving} data-testid="save-part-btn" className="bg-[var(--panel-2)] hover:bg-[var(--border-2)] text-[#c9a86a]">
          {saving ? "Saving…" : isEdit ? "Save Changes" : "Create Part"}
        </Button>
      </div>

      <Card className="p-6 border-[var(--border-lch)]">
        <SectionTitle>Identity</SectionTitle>
        <div className="grid sm:grid-cols-2 gap-4">
          <TextField label="Title *" value={form.title} onChange={(v) => set("title", v)} placeholder="e.g. Rolex Submariner Bezel Insert" testId="pf-title" />
          <div className="space-y-1.5">
            <Label className="text-xs text-[var(--text-2)]">Category</Label>
            <Select value={form.part_category} onValueChange={(v) => set("part_category", v)}>
              <SelectTrigger className="bg-[var(--panel)]" data-testid="pf-category"><SelectValue /></SelectTrigger>
              <SelectContent>{PART_CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        <SectionTitle>Compatibility</SectionTitle>
        <div className="grid sm:grid-cols-2 gap-4">
          <TextField label="Brand" value={form.brand} onChange={(v) => set("brand", v)} testId="pf-brand" />
          <TextField label="Model" value={form.model} onChange={(v) => set("model", v)} testId="pf-model" />
          <TextField label="Reference Number" value={form.reference_number} onChange={(v) => set("reference_number", v)} testId="pf-ref" />
          <TextField label="Part Number" value={form.part_number} onChange={(v) => set("part_number", v)} testId="pf-partnum" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4 mt-4">
          <TextField label="Compatible References (comma-separated)" value={compatRefText} onChange={setCompatRefText} placeholder="16610, 116610, 14060" testId="pf-compat-ref" />
          <TextField label="Compatible Models (comma-separated)" value={compatModelText} onChange={setCompatModelText} placeholder="Submariner, Sea-Dweller" testId="pf-compat-model" />
        </div>

        <SectionTitle>Condition &amp; Authenticity</SectionTitle>
        <div className="grid sm:grid-cols-3 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs text-[var(--text-2)]">Condition</Label>
            <Select value={form.condition || ""} onValueChange={(v) => set("condition", v)}>
              <SelectTrigger className="bg-[var(--panel)]" data-testid="pf-condition"><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent>{PART_CONDITIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-[var(--text-2)]">Origin</Label>
            <Select value={form.origin || ""} onValueChange={(v) => set("origin", v)}>
              <SelectTrigger className="bg-[var(--panel)]" data-testid="pf-origin"><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="oem">OEM / Factory</SelectItem>
                <SelectItem value="aftermarket">Aftermarket</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-[var(--text-2)]">Authentication</Label>
            <Select value={form.authentication_status} onValueChange={(v) => set("authentication_status", v)}>
              <SelectTrigger className="bg-[var(--panel)]" data-testid="pf-auth"><SelectValue /></SelectTrigger>
              <SelectContent>{AUTH_STATUSES.map((s) => <SelectItem key={s} value={s}>{titleCase(s)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        <SectionTitle>Availability &amp; Pricing</SectionTitle>
        <div className="grid sm:grid-cols-2 gap-4">
          <TextField label="Quantity" type="number" value={form.quantity} onChange={(v) => set("quantity", v)} testId="pf-qty" />
          <div className="space-y-1.5">
            <Label className="text-xs text-[var(--text-2)]">Status</Label>
            <Select value={form.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger className="bg-[var(--panel)]" data-testid="pf-status"><SelectValue /></SelectTrigger>
              <SelectContent>{PART_STATUSES.map((s) => <SelectItem key={s} value={s}>{titleCase(s)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-3 pt-6">
            <Switch checked={form.price_on_request} onCheckedChange={(v) => set("price_on_request", v)} data-testid="pf-por" />
            <Label className="text-sm">Price on Request</Label>
          </div>
          <TextField label="Selling Price (USD)" type="number" value={form.price} onChange={(v) => set("price", v)} placeholder="e.g. 850" testId="pf-price" />
        </div>
        <div className="mt-4 space-y-1.5">
          <Label className="text-xs text-[var(--text-2)]">Public Description</Label>
          <Textarea value={form.description ?? ""} onChange={(e) => set("description", e.target.value)} rows={3} className="bg-[var(--panel)]" data-testid="pf-desc" />
        </div>

        <SectionTitle>Public Photos</SectionTitle>
        <div className="space-y-3">
          <FileGrid files={form.public_photos} onRemove={(f) => set("public_photos", form.public_photos.filter((x) => x.id !== f.id))} />
          <FileUpload visibility="public" category="part_public" label="Add Public Photos" testId="upload-part-photo"
            onUploaded={(fs) => set("public_photos", [...form.public_photos, ...fs])} />
        </div>

        <SectionTitle locked>Private · Operational</SectionTitle>
        <div className="grid sm:grid-cols-2 gap-4">
          <TextField label="Source / Seller" value={form.source_seller} onChange={(v) => set("source_seller", v)} testId="pf-source" />
        </div>
        <div className="mt-4 space-y-1.5">
          <Label className="text-xs text-[var(--text-2)]">Internal Notes</Label>
          <Textarea value={form.internal_notes ?? ""} onChange={(e) => set("internal_notes", e.target.value)} rows={2} className="bg-[var(--panel)]" data-testid="pf-notes" />
        </div>

        {showFin && (
          <>
            <SectionTitle locked>Financials</SectionTitle>
            <div className="grid sm:grid-cols-2 gap-4">
              <TextField label="Cost Basis (USD)" type="number" value={form.cost_basis} onChange={(v) => set("cost_basis", v)} testId="pf-cost" />
            </div>
          </>
        )}

        <SectionTitle locked>Private Documents</SectionTitle>
        <div className="space-y-3">
          <Label className="text-xs text-[var(--text-2)]">Authentication Documents</Label>
          <FileGrid files={form.authentication_documents} onRemove={(f) => set("authentication_documents", form.authentication_documents.filter((x) => x.id !== f.id))} />
          <FileUpload visibility="private" accept="image/*,application/pdf" category="part_auth_doc" label="Upload Auth Docs" testId="upload-part-auth-doc"
            onUploaded={(fs) => set("authentication_documents", [...form.authentication_documents, ...fs])} />
        </div>

        <div className="mt-8 flex justify-end">
          <Button onClick={save} disabled={saving} className="bg-[var(--panel-2)] hover:bg-[var(--border-2)] text-[#c9a86a]">
            {saving ? "Saving…" : isEdit ? "Save Changes" : "Create Part"}
          </Button>
        </div>
      </Card>
    </div>
  );
}
